import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiClient, { formatApiError } from "@/api/client";
import {
  ChevronLeft, UploadCloud, ArrowRight, FileText, CheckCircle2, AlertCircle,
  Loader2, Search, Sparkles, X,
} from "lucide-react";
import { toast } from "sonner";
import CustomerSelect from "@/components/CustomerSelect";

const FIELD_LABELS = {
  keyword: "Anahtar Kelime / Arama Terimi",
  avg_monthly_searches: "Ort. Aylık Aramalar",
  competition: "Rekabet",
  competition_index: "Rekabet (Indexli)",
  top_bid_low: "Sayfa Başı Teklif (düşük)",
  top_bid_high: "Sayfa Başı Teklif (yüksek)",
  match_type: "Eşleşme Türü",
  added_excluded: "Eklendi / Hariç",
  impressions: "Gösterim",
  clicks: "Tıklama",
  ctr: "CTR",
  avg_cpc: "Ortalama TBM",
  cost: "Maliyet",
  conversions: "Dönüşüm",
  cost_per_conv: "Dönüşüm Başına Maliyet",
  conv_rate: "Dönüşüm Oranı",
};

const STEPS = ["Yükle", "Sütun Eşle", "Bilgi & Talimat", "Başlat"];

export default function KeywordNewJobPage() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [upload, setUpload] = useState(null); // server upload response
  const [mode, setMode] = useState("planner");
  const [mapping, setMapping] = useState({});
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [customerCtx, setCustomerCtx] = useState("");
  const [userPrompt, setUserPrompt] = useState("");
  const [countPos, setCountPos] = useState(100);
  const [countNeg, setCountNeg] = useState(50);
  const [matchTypes, setMatchTypes] = useState(["exact", "phrase"]);

  useEffect(() => {
    apiClient.get("/customers").then(r => setCustomers(r.data || [])).catch(()=>{});
  }, []);

  const onFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await apiClient.post("/ai-tools/keyword/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUpload(data);
      setMapping(data.auto_mapping || {});
      setStep(1);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const start = async () => {
    if (!upload) return;
    setSubmitting(true);
    try {
      await apiClient.post(`/ai-tools/keyword/jobs/${upload.job_id}/start`, {
        mode,
        mapping,
        customer_id: customerId || null,
        customer_context: customerCtx,
        user_prompt: userPrompt,
        count_positive: countPos,
        count_negative: countNeg,
        default_match_types: matchTypes,
      });
      toast.success("Analiz başlatıldı");
      nav(`/ai-araclar/anahtar-kelime/${upload.job_id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const toggleMatch = (t) => setMatchTypes(prev => prev.includes(t) ? prev.filter(x=>x!==t) : [...prev, t]);
  const hasKeywordMapped = mapping?.keyword !== null && mapping?.keyword !== undefined;

  const selectedCustomer = useMemo(()=>customers.find(c=>c.id===customerId), [customers, customerId]);
  useEffect(()=>{
    if (selectedCustomer && !customerCtx) {
      const parts = [
        selectedCustomer.company && `Şirket: ${selectedCustomer.company}`,
        selectedCustomer.industry && `Sektör: ${selectedCustomer.industry}`,
        selectedCustomer.notes && `Not: ${selectedCustomer.notes}`,
      ].filter(Boolean);
      if (parts.length) setCustomerCtx(parts.join("\n"));
    }
  }, [selectedCustomer]); // eslint-disable-line

  return (
    <div className="space-y-5" data-testid="keyword-new-page">
      <div className="flex items-center gap-2 text-xs text-[#64748B]">
        <Link to="/ai-araclar" className="hover:text-[#4F46E5]">AI Araçlar</Link>
        <span>/</span>
        <Link to="/ai-araclar/anahtar-kelime" className="hover:text-[#4F46E5]">Anahtar Kelime Analizi</Link>
        <span>/</span>
        <span className="font-medium text-[#0F172A]">Yeni Analiz</span>
      </div>

      <div>
        <h1 className="text-2xl font-bold">Yeni Analiz Oluştur</h1>
        <p className="text-sm text-[#64748B] mt-1">CSV yükle → sütunları eşle → müşteri profilini gir → AI seninle çalışmaya başlasın.</p>
      </div>

      {/* Steps */}
      <div className="ky-card p-4">
        <div className="flex items-center gap-3 overflow-x-auto">
          {STEPS.map((label, i) => (
            <React.Fragment key={label}>
              <div className={`flex items-center gap-2 ${i===step?"text-[#4F46E5]":i<step?"text-[#16A34A]":"text-[#94A3B8]"}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold ${i===step?"bg-[#4F46E5] text-white":i<step?"bg-[#16A34A] text-white":"bg-[#F4F4F5] text-[#64748B]"}`}>
                  {i<step ? <CheckCircle2 size={12}/> : i+1}
                </div>
                <span className="text-xs font-medium whitespace-nowrap">{label}</span>
              </div>
              {i<STEPS.length-1 && <div className={`h-px flex-1 min-w-[20px] ${i<step?"bg-[#16A34A]":"bg-[#ECECEF]"}`}/>}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step 0: Upload */}
      {step === 0 && (
        <div className="ky-card p-6">
          <h3 className="font-semibold text-base mb-1">CSV Dosyası Yükle</h3>
          <p className="text-xs text-[#64748B] mb-4">Google Keyword Planner çıktısı veya Google Ads Arama Terimleri raporu CSV'si.</p>

          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            <button onClick={()=>setMode("planner")} className={`text-left p-4 rounded-lg border-2 transition-all ${mode==="planner"?"border-[#4F46E5] bg-[#EEF2FF]":"border-[#ECECEF] hover:border-[#C7D2FE]"}`} data-testid="mode-planner">
              <div className="font-semibold text-sm mb-1">Keyword Planner</div>
              <p className="text-[11px] text-[#64748B]">Anahtar kelime fikirleri (hacim, rekabet, teklif aralığı)</p>
            </button>
            <button onClick={()=>setMode("search_terms")} className={`text-left p-4 rounded-lg border-2 transition-all ${mode==="search_terms"?"border-[#4F46E5] bg-[#EEF2FF]":"border-[#ECECEF] hover:border-[#C7D2FE]"}`} data-testid="mode-search">
              <div className="font-semibold text-sm mb-1">Arama Terimleri Raporu</div>
              <p className="text-[11px] text-[#64748B]">Mevcut kampanyalarınızdan gerçek arama terimleri (TBM, dönüşüm, maliyet)</p>
            </button>
          </div>

          <label htmlFor="csv-file" className="block border-2 border-dashed border-[#C7D2FE] rounded-xl p-10 text-center cursor-pointer hover:bg-[#FAFAFB] transition-colors">
            <UploadCloud size={36} className="mx-auto text-[#4F46E5] mb-2"/>
            <div className="text-sm font-medium">CSV dosyasını seçmek için tıkla</div>
            <div className="text-[11px] text-[#94A3B8] mt-1">veya dosyayı buraya sürükle bırak</div>
            <input id="csv-file" type="file" accept=".csv,text/csv,application/vnd.ms-excel" className="hidden" onChange={e=>onFile(e.target.files?.[0])} data-testid="csv-file-input"/>
          </label>
          {uploading && <div className="text-center text-sm text-[#64748B] mt-3"><Loader2 className="animate-spin inline mr-2" size={14}/>Yükleniyor…</div>}
        </div>
      )}

      {/* Step 1: Mapping */}
      {step === 1 && upload && (
        <div className="ky-card p-6 space-y-4">
          <div className="flex items-start gap-3">
            <FileText size={18} className="text-[#4F46E5] mt-0.5 shrink-0"/>
            <div>
              <h3 className="font-semibold text-base">{upload.filename}</h3>
              <p className="text-xs text-[#64748B]">{upload.total_rows.toLocaleString("tr-TR")} satır · {upload.headers.length} sütun · otomatik eşleme uygulandı</p>
            </div>
          </div>

          <div className="bg-[#FAFAFB] border border-[#ECECEF] rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="text-xs w-full">
                <thead className="bg-[#F4F4F5]">
                  <tr>{upload.headers.map((h,i) => <th key={i} className="text-left px-3 py-2 font-medium text-[#64748B]">{h || `Sütun ${i+1}`}</th>)}</tr>
                </thead>
                <tbody>
                  {upload.sample_rows.map((r,ri) => (
                    <tr key={ri} className="border-t border-[#ECECEF]">
                      {upload.headers.map((_, ci) => <td key={ci} className="px-3 py-2 text-[#0F172A] truncate max-w-[180px]">{r[ci] ?? ""}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-sm mb-2">Sütun Eşleştirme</h4>
            <p className="text-[11px] text-[#64748B] mb-3">Her bir kanonik alana CSV'deki uygun sütunu seç. Otomatik eşleşme yapıldıysa kontrol et.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {upload.available_fields.map(f => (
                <div key={f} className="flex items-center gap-2">
                  <label className="text-xs text-[#64748B] w-44 shrink-0 flex items-center gap-1.5">
                    {f === "keyword" && <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444]"/>}
                    {FIELD_LABELS[f] || f}
                  </label>
                  <select value={mapping[f] ?? ""} onChange={e=>setMapping({...mapping, [f]: e.target.value === "" ? null : Number(e.target.value)})} className="ky-input ky-input-sm flex-1" data-testid={`map-${f}`}>
                    <option value="">— seçili değil —</option>
                    {upload.headers.map((h,i) => <option key={i} value={i}>{h || `Sütun ${i+1}`}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-[#EF4444] mt-2">* Kırmızı işaretli alan zorunludur (Anahtar Kelime / Arama Terimi)</p>
          </div>

          <div className="flex justify-between pt-2 border-t border-[#ECECEF]">
            <button onClick={()=>setStep(0)} className="ky-btn ky-btn-outline">← Geri</button>
            <button onClick={()=>setStep(2)} disabled={!hasKeywordMapped} className="ky-btn ky-btn-accent" data-testid="step-next-1">Devam <ArrowRight size={14}/></button>
          </div>
        </div>
      )}

      {/* Step 2: Customer context */}
      {step === 2 && upload && (
        <div className="ky-card p-6 space-y-4">
          <div>
            <h3 className="font-semibold text-base mb-1">Müşteri ve Talimatlar</h3>
            <p className="text-xs text-[#64748B]">AI'a müşterinin profili ve özel talimatlarını ver. Bu bilgi sonuç kalitesini doğrudan etkiler.</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#64748B] mb-1.5">Müşteri (opsiyonel — seçilirse portala yayınlanabilir)</label>
            <CustomerSelect customers={customers} value={customerId || null} onChange={(v) => setCustomerId(v || "")} placeholder="— müşteri seçilmedi —" testid="customer-select"/>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#64748B] mb-1.5">Müşteri Profili / Hizmet Bilgileri *</label>
            <textarea value={customerCtx} onChange={e=>setCustomerCtx(e.target.value)} rows={5} className="ky-textarea text-sm" data-testid="customer-context" placeholder="Örn: İstanbul Avrupa Yakası'nda hizmet veren özel diş kliniği. Hizmetler: implant, ortodonti, estetik diş hekimliği. Hedef kitle: 25-55 yaş arası, orta-üst gelir grubu. Bütçe odaklı müşteriler hedeflenmiyor."/>
            <p className="text-[10px] text-[#94A3B8] mt-1">Lokasyon, hizmetler, hedef kitle, marka tonu — ne kadar detaylı yazarsan AI o kadar isabetli sonuç verir.</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#64748B] mb-1.5">Özel Talimat (opsiyonel)</label>
            <textarea value={userPrompt} onChange={e=>setUserPrompt(e.target.value)} rows={3} className="ky-textarea text-sm" data-testid="user-prompt" placeholder="Örn: Marka adı içeren kelimeleri ayrı grupla. Ücretsiz/bedava içeren kelimeleri kesinlikle negatife at."/>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#64748B] mb-1.5">Pozitif Kelime Sayısı (max)</label>
              <input type="number" min={10} max={300} value={countPos} onChange={e=>setCountPos(Number(e.target.value))} className="ky-input" data-testid="count-pos"/>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#64748B] mb-1.5">Negatif Kelime Sayısı (max)</label>
              <input type="number" min={5} max={200} value={countNeg} onChange={e=>setCountNeg(Number(e.target.value))} className="ky-input" data-testid="count-neg"/>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#64748B] mb-1.5">Varsayılan Eşleşme Türü Tercihleri (AI önerirken kullanır)</label>
            <div className="flex gap-2">
              {["exact","phrase","broad"].map(t => (
                <button key={t} onClick={()=>toggleMatch(t)} className={`px-3 py-1.5 rounded-md text-xs font-medium border-2 transition-all ${matchTypes.includes(t)?"border-[#4F46E5] bg-[#EEF2FF] text-[#4F46E5]":"border-[#ECECEF] text-[#64748B]"}`} data-testid={`mt-${t}`}>
                  {t === "exact" ? "Tam Eşleşme" : t === "phrase" ? "Sıralı Eşleşme" : "Geniş Eşleşme"}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-between pt-2 border-t border-[#ECECEF]">
            <button onClick={()=>setStep(1)} className="ky-btn ky-btn-outline">← Geri</button>
            <button onClick={()=>setStep(3)} disabled={!customerCtx.trim()} className="ky-btn ky-btn-accent" data-testid="step-next-2">Özet <ArrowRight size={14}/></button>
          </div>
        </div>
      )}

      {/* Step 3: Summary + start */}
      {step === 3 && upload && (
        <div className="ky-card p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-[#4F46E5]"/>
            <h3 className="font-semibold text-base">Hazır</h3>
          </div>
          <div className="bg-[#FAFAFB] border border-[#ECECEF] rounded-lg p-4 text-xs space-y-2">
            <Row label="Dosya" value={upload.filename}/>
            <Row label="Mod" value={mode === "planner" ? "Keyword Planner" : "Arama Terimleri"}/>
            <Row label="Satır sayısı" value={upload.total_rows.toLocaleString("tr-TR")}/>
            <Row label="Müşteri" value={selectedCustomer?.name || "(seçilmedi)"}/>
            <Row label="Hedef sayı" value={`${countPos} pozitif · ${countNeg} negatif`}/>
            <Row label="Eşleşme türleri" value={matchTypes.join(", ")}/>
          </div>
          <div className="bg-[#EEF2FF] border border-[#C7D2FE] rounded-lg p-3 text-[11px] text-[#3730A3] flex items-start gap-2">
            <AlertCircle size={14} className="shrink-0 mt-0.5"/>
            <div>Büyük CSV'ler için analiz birkaç dakika sürebilir. Sayfayı kapatabilirsiniz — analiz arka planda devam eder ve sonuç sayfası kendini günceller.</div>
          </div>
          <div className="flex justify-between pt-2 border-t border-[#ECECEF]">
            <button onClick={()=>setStep(2)} className="ky-btn ky-btn-outline">← Geri</button>
            <button onClick={start} disabled={submitting} className="ky-btn ky-btn-accent" data-testid="start-analysis">
              {submitting ? <><Loader2 size={14} className="animate-spin"/>Başlatılıyor…</> : <><Sparkles size={14}/>Analizi Başlat</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3"><span className="text-[#94A3B8]">{label}</span><span className="font-medium text-[#0F172A] text-right">{value}</span></div>
);
